import React from 'react';
import Script from 'next/script';
import cx from 'classnames';
import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from '@contentful/live-preview/react';

// components
import { usePaywallProvider, ModalPane } from 'src/components/Surfaces';
import BtnPrimary from 'src/components-v2/Inputs/Button/BtnPrimary';
import BtnSecondary from 'src/components-v2/Inputs/Button/BtnSecondary';
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';

// helpers
import {
  FormTypes,
  PaywallButtonAppearances,
  PaywallRenderOptions,
  PAYWALL_SELECTOR,
} from 'src/lib/constants';

// styles
import styles from './paywall.module.scss';

type ButtonT = {
  buttonText?: string;
};

interface Props {
  button?: ButtonT;
  children: React.ReactNode;
  fields: Record<string, unknown>;
  sys?: { id?: string };
  trackingId: string;
  hasGradient?: boolean;
}

const Paywall: React.FC<Props> = ({
  button,
  children,
  trackingId,
  fields,
  sys,
  hasGradient = true,
}) => {
  const liveEntry = useContentfulLiveUpdates({ sys, fields });
  const liveFields = liveEntry?.fields ?? fields;
  const inspectorProps = useContentfulInspectorMode({
    entryId: liveEntry?.sys?.id ?? sys?.id,
  });
  const appearance = liveFields?.appearance ?? liveFields?.renderOption;
  const buttonText =
    (typeof liveFields?.paywallButtonText === 'string' &&
      liveFields.paywallButtonText) ||
    button?.buttonText;
  const PaywallButton =
    liveFields?.paywallButtonAppearance === PaywallButtonAppearances.SECONDARY
      ? BtnSecondary
      : BtnPrimary;
  const {
    setIsModalOpen,
    isModalOpen,
    handleUnlockContent,
    isContentUnlocked,
    renderOption,
  } = usePaywallProvider();
  const activeRenderOption = appearance || renderOption;
  return (
    <>
      {!isContentUnlocked && (
        <div
          className={cx(styles.paywall, {
            [styles.noBgGradient]: !hasGradient,
          })}
          {...inspectorProps({ fieldId: 'internalTitle' })}
        >
          {activeRenderOption === PaywallRenderOptions.MODAL && (
            <ModalPane
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              trackingId={trackingId}
            >
              <div {...inspectorProps({ fieldId: 'components' })}>
                <CmsComponentSwitch
                  components={[liveFields?.components]}
                  handleFormSubmit={handleUnlockContent}
                  formType={FormTypes.CMS}
                  trackingId={trackingId?.toUpperCase()}
                  usePageTheme
                />
              </div>
            </ModalPane>
          )}

          {buttonText && (
            <div
              className={styles.btnContainer}
              {...inspectorProps({ fieldId: 'paywallButtonText' })}
            >
              <PaywallButton
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                {buttonText}
              </PaywallButton>
            </div>
          )}
        </div>
      )}
      {/*
         We have to make the content visible to search engines so we don't get penalized for cloaking.
         This is why we only apply a class to hide the content instead of not rendering it
       */}
      <div
        data-render-option={appearance}
        className={cx(PAYWALL_SELECTOR.replace('.', ''), {
          [styles.hiddenContent]: !isContentUnlocked,
        })}
        {...inspectorProps({ fieldId: 'internalTitle' })}
      >
        {children}
      </div>
      <Script
        id={`${trackingId}_SCHEMA` || 'customer-story-paywall-schema'}
        type='application/ld+json'
        strategy='beforeInteractive'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            isAccessibleForFree: 'False',
            hasPart: {
              '@type': 'WebPageElement',
              isAccessibleForFree: 'False',
              cssSelector: PAYWALL_SELECTOR,
            },
          }),
        }}
      />
    </>
  );
};

export default Paywall;
